import { useSyncExternalStore } from "react";
import { cachedRoutine } from "./routineCache";
import { claimRoutine, routineRequest, uploadRoutineFile } from "./routineApi";
import type { UploadTicket } from "./routineApi";
import type { RoutineProgress, RoutineSnapshot } from "./routineTypes";

type Task = {
  playlistId: string;
  label: string;
  progress: RoutineProgress;
  error: string | null;
  busy: boolean;
};
let task: Task | null = null;
let controller: AbortController | null = null;
const listeners = new Set<() => void>();
const update = (value: Task | null) => {
  task = value;
  listeners.forEach((fn) => fn());
};
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
export const useRoutineTask = () =>
  useSyncExternalStore(
    subscribe,
    () => task,
    () => null,
  );
export const getRoutineTask = () => task;
export function clearRoutineTask() {
  if (!task?.busy) update(null);
}
export function cancelRoutineTask() {
  controller?.abort();
}

export async function runBrowserTask(
  playlistId: string,
  label: string,
  run: (
    signal: AbortSignal,
    progress: (p: RoutineProgress) => void,
  ) => Promise<void | string>,
) {
  if (task?.busy)
    throw new Error("Another video is being processed. Wait for it to finish.");
  controller = new AbortController();
  const currentController = controller;
  update({
    playlistId,
    label,
    busy: true,
    error: null,
    progress: { stage: "Preparing" },
  });
  const beforeUnload = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = "";
  };
  const linkGuard = (event: MouseEvent) => {
    if (
      event.defaultPrevented ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    const link = (event.target as Element | null)?.closest?.(
      "a[href]",
    ) as HTMLAnchorElement | null;
    if (
      !link ||
      link.target === "_blank" ||
      link.download ||
      link.href === location.href
    )
      return;
    if (
      !window.confirm(
        "A video is still being created or uploaded. Keep this tab open until it is saved. Leave this page?",
      )
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  window.addEventListener("beforeunload", beforeUnload);
  document.addEventListener("click", linkGuard, true);
  try {
    const completion = await run(currentController.signal, (progress) =>
      update(task ? { ...task, progress } : null),
    );
    update({
      playlistId,
      label,
      busy: false,
      error: null,
      progress: { stage: completion || "Video saved" },
    });
  } catch (error) {
    const message = currentController.signal.aborted
      ? "Processing stopped. Your saved reviews are safe. Retry when ready."
      : error instanceof Error
        ? error.message
        : String(error);
    update({
      playlistId,
      label,
      busy: false,
      error: message,
      progress: { stage: "Action needed" },
    });
    throw new Error(message);
  } finally {
    window.removeEventListener("beforeunload", beforeUnload);
    document.removeEventListener("click", linkGuard, true);
    controller = null;
    window.dispatchEvent(new Event("bollyrhythm:routine-updated"));
  }
}

export async function generateRoutine(
  snapshot: RoutineSnapshot,
  saveRanges?: () => Promise<RoutineSnapshot>,
) {
  return runBrowserTask(
    snapshot.playlist_id,
    snapshot.name,
    async (signal, progress) => {
      if (saveRanges) {
        progress({ stage: "Saving selected ranges" });
        snapshot = await saveRanges();
      }
      signal.throwIfAborted();
      progress({ stage: "Preparing performance videos" });
      const prepared = await routineRequest<{ fingerprint: string }>(
        "prepare",
        {
          playlist_id: snapshot.playlist_id,
          fingerprint: snapshot.fingerprint,
        },
      );
      signal.throwIfAborted();
      const claimed = await claimRoutine(
        snapshot.playlist_id,
        prepared.fingerprint,
      );
      if ("already_current" in claimed) return;
      const context = { playlist_id: snapshot.playlist_id, job_id: claimed.id };
      let fatal: Error | null = null;
      let heartbeatPending = false;
      let remoteUploadStarted = false;
      const heartbeat = window.setInterval(() => {
        if (heartbeatPending) return;
        heartbeatPending = true;
        void routineRequest("heartbeat", context)
          .catch((error) => {
            // An offline request may recover; a rejected job must not publish.
            if (
              /changed|no longer active|expired|access/i.test(String(error))
            ) {
              fatal = error;
              controller?.abort();
            }
          })
          .finally(() => {
            heartbeatPending = false;
          });
      }, 20000);
      try {
        let cached;
        try {
          cached = await cachedRoutine(snapshot.playlist_id);
        } catch {
          /* storage is optional */
        }
        let blob =
          cached?.fingerprint === claimed.fingerprint ? cached.blob : undefined;
        if (!blob) {
          const { renderRoutine } = await import("./routineRenderer");
          blob = await renderRoutine(
            claimed.clips,
            async (clip) => {
              const result = await routineRequest<{ url: string }>(
                "render_source",
                { ...context, entry_id: clip.entry_id },
              );
              return result.url;
            },
            progress,
            signal,
          );
          try {
            await cachedRoutine(snapshot.playlist_id, {
              fingerprint: claimed.fingerprint,
              blob,
              savedAt: Date.now(),
            });
          } catch {
            /* continue when quota is unavailable */
          }
        }
        signal.throwIfAborted();
        await routineRequest("heartbeat", context);
        remoteUploadStarted = claimed.provider === "vimeo";
        const ticket = await routineRequest<UploadTicket>(
          "render_upload",
          { ...context, size: blob.size },
        );
        await uploadRoutineFile(ticket, blob, signal, (percent) =>
          progress({
            stage: "Uploading routine - keep this tab open",
            percent,
          }),
        );
        signal.throwIfAborted();
        progress({ stage: "Saving routine" });
        const completion = await routineRequest<{ status?: string }>("complete", context);
        try {
          await cachedRoutine(snapshot.playlist_id, null);
        } catch {
          /* best effort */
        }
        if (["processing", "verifying"].includes(completion?.status ?? ""))
          return "Your complete routine is processing.";
      } catch (error) {
        const reason = fatal ?? error;
        // A disconnected browser must not cancel Vimeo's durable processing or
        // release an uncertain upload reservation and create duplicate videos.
        if (!remoteUploadStarted) await routineRequest("fail", {
          ...context,
          error:
            reason instanceof Error ? reason.message : "Generation interrupted",
        }).catch(() => {});
        throw reason;
      } finally {
        window.clearInterval(heartbeat);
      }
    },
  );
}
